import React, { useContext, FC, ReactNode } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import set from 'lodash/set';
import styled from 'styled-components';

import Dropdown from 'src/presentation/components/forms/Dropdown';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import FormContext from 'src/presentation/contexts/FormContext';
import generateId from 'src/lib/generateId';
import { EditorSection, PrefixContainer } from 'src/presentation/components/pages/shared/EditorSectionLayout';
import {
  FIELDS_MAPPER,
  DISPLAY_RULES_PAGES_OPTIONS,
  DISPLAY_RULES_EXCEPTIONS_OPTIONS,
} from 'src/presentation/components/pages/chat/chatConstants';
import { getSelectorOptions } from 'src/presentation/components/pages/shared/utils/Utils';
import InputField from 'src/presentation/components/forms/InputField';
import { ErrorLabel, ControlLabel } from '@ad-scripts/presentation/components/common/forms/control_container';
import { H4 } from '@ad-scripts/presentation/components/common/headers';
import { Section } from 'src/presentation/components/forms/Layout';
import { CHAT_BRAND } from 'src/constants/branding';
import { MediumHeader } from '../ChatLayout';

export type RulePageOptionType = {
  label: string;
  value: boolean;
};

export type ExceptionType = {
  type: string;
  id: string;
  displayDelay: number;
  content: {
    matchType: string;
    value: string;
  };
};

type ChatRulesSection = {
  errors: any[];
};

const ChatRulesSection: FC<ChatRulesSection> = ({ errors }) => {
  return (
    <Section>
      <MediumHeader>Display Rules</MediumHeader>
      <FieldContainer
        description={`Set what pages you want ${CHAT_BRAND.name} to display on.`}
        data-aid="configuration.displayRules"
        control={<ExceptionSection errors={errors} />}
      />
    </Section>
  );
};
const PageSelectContent: FC<any> = () => {
  const { workingState, updateState } = useContext(FormContext);
  const { DISPLAY_RULES_PAGE } = FIELDS_MAPPER;
  const allPagesEnabled = get(workingState, DISPLAY_RULES_PAGE.path, DISPLAY_RULES_PAGE.defaultValue);
  const rulePageOptions: RulePageOptionType[] = getSelectorOptions(DISPLAY_RULES_PAGES_OPTIONS);
  const selectedDropdownOption = allPagesEnabled
    ? DISPLAY_RULES_PAGES_OPTIONS.ALL_PAGES
    : DISPLAY_RULES_PAGES_OPTIONS.NO_PAGES;

  const onMatchTypeChanged = showByDefault => {
    const changes = set({}, DISPLAY_RULES_PAGE.path, showByDefault);

    updateState(changes, { replaceArrayValues: true });
  };

  return (
    <StyledSectionRow title={DISPLAY_RULES_PAGE.label}>
      <StyledDropdown
        allowEmptyValues
        className={DISPLAY_RULES_PAGE.path}
        label={DISPLAY_RULES_PAGE.description}
        attr={DISPLAY_RULES_PAGE.path}
        data-aid={DISPLAY_RULES_PAGE.path}
        options={rulePageOptions}
        text={selectedDropdownOption.label}
        value={selectedDropdownOption.id}
        onChange={onMatchTypeChanged}
      />
    </StyledSectionRow>
  );
};

type ExceptionSectionProps = {
  errors: any[];
};

const ExceptionSection: FC<ExceptionSectionProps> = ({ errors }) => {
  const { workingState } = useContext(FormContext);
  const { DISPLAY_RULES_EXCEPTIONS } = FIELDS_MAPPER;

  const rules: ExceptionType[] = get(
    workingState,
    DISPLAY_RULES_EXCEPTIONS.path,
    DISPLAY_RULES_EXCEPTIONS.defaultValue
  );

  const renderItem = ({ id, index }): ReactNode | null => {
    const item: ExceptionType | undefined = find(rules, rule => rule.id === id);
    if (!item) return null;

    const attrPrefix = `${DISPLAY_RULES_EXCEPTIONS.path}[${index}]`;
    const aidPrefix = `item-row[${index}]`;

    const selectAttrValue = `${attrPrefix}.content.matchType`;
    const selectDataAidValue = `${aidPrefix}-content-matchType`;

    const inputAttrValue = `${attrPrefix}.content.value`;
    const inputDataAidValue = `${aidPrefix}-content-value`;

    return (
      <StyledWrappedRow>
        <span>
          <StyledDropdown
            attr={selectAttrValue}
            className={selectAttrValue}
            data-aid={selectDataAidValue}
            options={getSelectorOptions(DISPLAY_RULES_EXCEPTIONS_OPTIONS)}
          />
        </span>
        <StyledInput attr={inputAttrValue} className="styledInput" data-aid={inputDataAidValue} />
      </StyledWrappedRow>
    );
  };

  const getNewItemDefaultData = (): ExceptionType => ({
    type: 'URL',
    id: generateId(),
    displayDelay: 0,
    content: {
      matchType: DISPLAY_RULES_EXCEPTIONS_OPTIONS.CONTAINS.id,
      value: '',
    },
  });

  const prefixWord = ({ index }) => {
    return (index > 0 ? 'or  ' : 'When ') + DISPLAY_RULES_EXCEPTIONS.label;
  };

  return (
    <StyledEditorSection
      data-aid="configuration.displayRules"
      addButtonLabel="Add Exception"
      attr={DISPLAY_RULES_EXCEPTIONS.path}
      content={<PageSelectContent />}
      getNewItemDefaultData={getNewItemDefaultData}
      renderItem={renderItem}
      title="Exceptions"
      errors={errors}
      prefixWord={prefixWord}
    />
  );
};

const StyledWrappedRow = styled.div`
  padding-top: 4px;
  :first-child {
    padding-top: 0px;
  }
  display: flex;
  margin-right: 0px;
  align-items: center;
`;

const StyledSectionRowWrap = styled.div`
  margin-bottom: 16px;
`;

const StyledSectionRowTitle = styled(H4)`
  padding-left: 16px;
  font-weight: bold;
`;
const StyledSectionRowContent = styled.div`
  padding-left: 8px;
  display: flex;
  align-items: center;
`;

const StyledSectionRow = ({ title, children }) => (
  <StyledSectionRowWrap>
    <StyledSectionRowTitle>{title}</StyledSectionRowTitle>
    {children && <StyledSectionRowContent>{children}</StyledSectionRowContent>}
  </StyledSectionRowWrap>
);

const StyledEditorSection = styled(EditorSection)`
  min-width: 100%;
  ${PrefixContainer} {
    width: 100px;
  }
  ${H4} {
    font-weight: bold;
  }
`;
const StyledDropdown = styled(Dropdown)`
  flex-direction: row;
  flex-grow: 0;
  ${ControlLabel} {
    padding-left: 8px;
    padding-top: 11px;
    font-weight: inherit;
    margin-bottom: 0;
    margin-right: 8px;
  }
`;

const StyledInput = styled(InputField)`
  flex-grow: 1;
  padding-left: 16px;
  ${ErrorLabel} {
    display: none;
  }
`;

export default ChatRulesSection;
